import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { getDocumentTranslator } from "./documentPopupMessages";
import "../../PurchasingUi.css";
import "./PurchasingDocumentPopups.css";

export default function PurchasingDocumentDialog({
  isOpen, title, message, confirmLabel, cancelLabel, onConfirm, onClose,
  isBusy = false, confirmDisabled = false, showCancel = true, tone = "primary", zIndex = 100050,
}) {
  const { themeId, languageId } = usePmsTheme();
  const t = getDocumentTranslator(languageId);
  const cardRef = useRef(null);
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousFocus = document.activeElement;
    const card = cardRef.current;
    const focusable = () => [...card.querySelectorAll('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')];
    const focusFirst = () => (focusable()[0] || card).focus();
    focusFirst();
    const keepFocus = (event) => { if (!card.contains(event.target)) focusFirst(); };
    const trapTab = (event) => {
      if (event.key !== "Tab") return;
      const elements = focusable();
      const first = elements[0] || card;
      const last = elements[elements.length - 1] || card;
      if (!card.contains(document.activeElement) || document.activeElement === card) {
        event.preventDefault(); (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener("focusin", keepFocus);
    document.addEventListener("keydown", trapTab);
    return () => {
      document.removeEventListener("focusin", keepFocus);
      document.removeEventListener("keydown", trapTab);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event) => { if (event.key === "Escape" && !isBusy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, isBusy, onClose]);
  if (!isOpen) return null;
  return createPortal(
    <div className="pilcore-purchasing-overlay pu-document-confirm" data-theme={themeId} style={{ zIndex }}
      onMouseDown={(event) => { if (event.target === event.currentTarget && !isBusy) onClose(); }}>
      <section className="pu-document-confirm__card" ref={cardRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
        <header><span>{t("Pilcore · Purchasing")}</span><h2>{title}</h2></header>
        <div className="pu-document-confirm__body">{typeof message === "string" ? <p>{message}</p> : message}</div>
        <footer>
          {showCancel && <button type="button" onClick={onClose} disabled={isBusy}>{cancelLabel || t("Cancel")}</button>}
          <button type="button" className={`pu-document-confirm__submit ${tone === "warning" ? "pu-document-confirm__submit--danger" : tone === "success" ? "pu-document-confirm__submit--success" : ""}`}
            onClick={onConfirm} disabled={isBusy || confirmDisabled}>{confirmLabel || t("Confirm")}</button>
        </footer>
      </section>
    </div>, document.body
  );
}

/** Promise-based confirmations preserve the existing save/delete sequencing. */
export function useDocumentDialog() {
  const { languageId } = usePmsTheme();
  const t = getDocumentTranslator(languageId);
  const [request, setRequest] = useState(null);
  const resolver = useRef(null);
  const settle = useCallback((result) => {
    resolver.current?.(result);
    resolver.current = null;
    setRequest(null);
  }, []);
  useEffect(() => () => { resolver.current?.(false); }, []);
  const open = (message, notification, tone, title) => new Promise((resolve) => {
    resolver.current?.(false);
    resolver.current = resolve;
    setRequest({ message, notification, tone, title });
  });
  return {
    isOpen: Boolean(request),
    confirm: (message, { tone = "warning" } = {}) => open(message, false, tone),
    notify: (message, { title } = {}) => open(message, true, undefined, title),
    dialog: <PurchasingDocumentDialog isOpen={Boolean(request)} title={request?.title || t(request?.notification ? "Pilcore · Purchasing" : "Confirm")}
      message={request?.message} showCancel={!request?.notification} confirmLabel={t(request?.notification ? "Close" : "Confirm")}
      tone={request?.notification ? "primary" : request?.tone} onConfirm={() => settle(true)} onClose={() => settle(false)} />,
  };
}
